/**
 * errorReporter.ts
 * Sender feil til backend som oppretter GitHub Issues automatisk.
 * Kun aktivt i produksjon (VITE_ERROR_REPORTING=true eller MODE=production).
 */

import { API_BASE } from '@/lib/config'
const ENABLED  = import.meta.env.PROD || import.meta.env.VITE_ERROR_REPORTING === 'true'

// Lokal dedup: ikke send samme feil mer enn én gang per 5 min
const _recentErrors = new Map<string, number>()
const DEDUP_MS = 5 * 60 * 1000

function _hash(msg: string): string {
  return msg.slice(0, 120)
}

function _isDuplicate(msg: string): boolean {
  const key  = _hash(msg)
  const last = _recentErrors.get(key)
  const now  = Date.now()
  if (last && now - last < DEDUP_MS) return true
  _recentErrors.set(key, now)
  // Trim cache
  if (_recentErrors.size > 100) {
    const cutoff = now - DEDUP_MS
    for (const [k, v] of _recentErrors) {
      if (v < cutoff) _recentErrors.delete(k)
    }
  }
  return false
}

export interface ErrorReportPayload {
  type:       'frontend' | 'agent' | 'ws' | 'api'
  message:    string
  stack?:     string
  url?:       string
  component?: string
  extra?:     Record<string, unknown>
}

export function reportError(payload: ErrorReportPayload): void {
  if (!ENABLED) return
  if (_isDuplicate(payload.message)) return

  const body = JSON.stringify({
    ...payload,
    url:       payload.url       ?? window.location.href,
    timestamp: new Date().toISOString(),
    extra:     {
      userAgent: navigator.userAgent,
      ...payload.extra,
    },
  })

  // Fire-and-forget — never throw
  fetch(`${API_BASE}/api/debug/log-error`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  }).catch(() => { /* silent */ })
}

/**
 * Koble til window.onerror og window.onunhandledrejection.
 * Kall én gang fra main.tsx.
 */
export function setupGlobalErrorReporting(): void {
  if (!ENABLED) return

  window.addEventListener('error', (event) => {
    // Skip ResizeObserver noise and cross-origin script errors
    if (!event.message || event.message === 'Script error.') return
    if (event.message.includes('ResizeObserver')) return

    reportError({
      type:    'frontend',
      message: event.message,
      stack:   event.error?.stack,
      url:     event.filename ?? window.location.href,
      extra:   { line: event.lineno, col: event.colno },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message ?? String(event.reason ?? 'Unhandled promise rejection')
    if (!msg || msg === 'undefined') return

    reportError({
      type:    'frontend',
      message: msg,
      stack:   event.reason?.stack,
      extra:   { source: 'unhandledRejection' },
    })
  })
}
